import { useState } from "react";
import { ChevronDown, Eye, EyeOff } from "lucide-react";
import type { FilterDto, Recipient } from "../../../shared/types";
import type { SearchCriteria } from "../../hooks/useApi";
import { OTHER_FILTER_ID, badgeClass } from "../filterColors";

export type SearchSubmit =
  | {
      mode: "criteria";
      criteria: SearchCriteria;
      /** Filter ids (incl. OTHER_FILTER_ID) whose messages stay visible; null = no badge constraint. */
      badgesOn: number[] | null;
    }
  | { mode: "ai"; prompt: string };

export function SearchDialog({
  filters,
  senders,
  searching,
  error,
  onSubmit,
  onClose,
}: {
  filters: FilterDto[];
  /** Distinct senders from the loaded messages, for the From quick-pick. */
  senders: Recipient[];
  searching: boolean;
  error: string | null;
  onSubmit: (submit: SearchSubmit) => void;
  onClose: () => void;
}) {
  const [mode, setMode] = useState<"criteria" | "ai">("criteria");
  const [from, setFrom] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  // All badges (incl. "Other" = unlabeled) start ON; turning some off restricts results.
  const [badgesOn, setBadgesOn] = useState<Set<number>>(
    () => new Set([...filters.map((f) => f.id), OTHER_FILTER_ID]),
  );
  const [prompt, setPrompt] = useState("");

  const allOn = badgesOn.size === filters.length + 1;

  const toggleBadge = (id: number) => {
    setBadgesOn((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const canSearch =
    mode === "ai"
      ? prompt.trim().length > 0
      : Boolean(from.trim() || subject.trim() || body.trim() || !allOn);

  const submit = () => {
    if (mode === "ai") {
      onSubmit({ mode: "ai", prompt: prompt.trim() });
    } else {
      onSubmit({
        mode: "criteria",
        criteria: {
          from: from.trim() || undefined,
          subject: subject.trim() || undefined,
          body: body.trim() || undefined,
        },
        badgesOn: allOn ? null : [...badgesOn],
      });
    }
  };

  const inputClass =
    "w-full rounded border border-stroke px-2 py-1 text-[13px] outline-none focus:border-accent";
  const labelClass = "pb-0.5 pt-1.5 text-[12px] font-medium text-ink-muted";

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/30" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div
        className="w-[440px] max-w-[92vw] rounded-lg border border-stroke bg-white p-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between pb-2">
          <h2 className="text-[15px] font-semibold">Search mail</h2>
          <button
            onClick={onClose}
            aria-label="Close search"
            className="rounded px-2 text-[16px] text-ink-muted hover:bg-black/5"
          >
            ×
          </button>
        </div>

        <div className="flex gap-1 rounded bg-surface p-0.5">
          {(["criteria", "ai"] as const).map((m) => (
            <button
              key={m}
              onClick={() => setMode(m)}
              className={`flex-1 rounded py-1 text-[12px] font-medium ${
                mode === m ? "bg-white text-accent shadow-sm" : "text-ink-muted hover:text-ink"
              }`}
            >
              {m === "criteria" ? "Filters" : "AI prompt"}
            </button>
          ))}
        </div>

        {mode === "criteria" ? (
          <div>
            <p className={labelClass}>From (sender)</p>
            <div className="flex gap-1.5">
              <input
                className={`${inputClass} min-w-0 flex-1`}
                placeholder="e.g. shopify.com or a name"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                autoFocus
              />
              {senders.length > 0 && (
                <div className="relative shrink-0" title="Pick a sender from the loaded messages">
                  <select
                    className="h-full w-7 cursor-pointer appearance-none rounded border border-stroke bg-white text-transparent outline-none focus:border-accent"
                    value=""
                    aria-label="Pick a sender from the loaded messages"
                    onChange={(e) => e.target.value && setFrom(e.target.value)}
                  >
                    <option value="" disabled />
                    {senders.map((s) => (
                      <option key={s.address} value={s.address}>
                        {s.name && s.name !== s.address ? `${s.name} (${s.address})` : s.address}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    size={14}
                    className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-ink-muted"
                  />
                </div>
              )}
            </div>
            {filters.length > 0 && (
              <>
                <p className={labelClass}>Badges (turn off to exclude)</p>
                <div className="flex flex-wrap gap-1.5">
                  {[
                    ...filters.map((f) => ({ id: f.id, name: f.name, color: f.color as string })),
                    { id: OTHER_FILTER_ID, name: "Other", color: "gray" },
                  ].map((b) => {
                    const on = badgesOn.has(b.id);
                    return (
                      <button
                        key={b.id}
                        type="button"
                        onClick={() => toggleBadge(b.id)}
                        title={on ? `Exclude ${b.name} messages` : `Include ${b.name} messages`}
                        className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${badgeClass(b.color)} ${
                          on ? "" : "opacity-45"
                        }`}
                      >
                        {on ? <Eye size={12} /> : <EyeOff size={12} />}
                        {b.name}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
            <p className={labelClass}>Subject includes</p>
            <input
              className={inputClass}
              placeholder="words in the subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
            <p className={labelClass}>Body includes</p>
            <input
              className={inputClass}
              placeholder="words in the message body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />
            <p className="pt-2 text-[11px] text-ink-muted">
              From/subject/body search your whole mailbox. Badge applies to already-classified
              messages.
            </p>
          </div>
        ) : (
          <div>
            <p className={labelClass}>What are you looking for?</p>
            <textarea
              className={`${inputClass} h-24 resize-y`}
              placeholder="e.g. emails about flight bookings or anything with a deadline this week"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              autoFocus
            />
            <p className="pt-2 text-[11px] text-ink-muted">
              The AI checks the messages currently loaded in the list (subject, sender, preview).
            </p>
          </div>
        )}

        {error && <p className="pt-2 text-[12px] text-red-700">{error}</p>}

        <div className="flex justify-end gap-2 pt-3">
          <button
            onClick={onClose}
            className="rounded border border-stroke px-4 py-1.5 text-[13px] hover:bg-black/5"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={!canSearch || searching}
            className="rounded bg-accent px-4 py-1.5 text-[13px] font-medium text-white hover:bg-accent-hover disabled:opacity-50"
          >
            {searching ? "Searching…" : "Search"}
          </button>
        </div>
      </div>
    </div>
  );
}
